// /**
//  * Seller Orders - list
//  */

// React
import React, {useContext} from 'react';
import {Link} from 'react-router-dom';

// Context
import AuthContext from '../../authContext';

// UI
import theme from '../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiFileDocumentOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiHistory,
  mdiClose,
  mdiPencilOutline,
  mdiPlusBoxMultiple,
} from '@mdi/js';

// Helper
import {translate, checkMatchMedia} from '../../utils/helpers';
import jMoment from 'moment-jalaali';
import Can from '../../components/Can';
// Type
import {Order} from './interfaces';

type Data = Order;
type Props = {
  data: any;
  handleOpenConfirmCancel: (id: number) => void;
};
type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: '#D5DADD !important',
      color: '#212121',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 750,
      borderRadius: '0',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  let headCells: HeadCell[] = [
    {
      id: 'customerName',
      label: 'نام سفارش‌دهنده',
    },
    {id: 'id', label: 'شناسه سفارش'},
    {id: 'trackingCode', label: 'کد پیگیری'},
    {id: 'deliveryType', label: 'نوع ارسال'},
    {
      id: 'deliveryDate',
      label: 'زمان تحویل',
    },
    {id: 'status', label: 'وضعیت سفارش'},
    {id: 'codValue', label: 'شیوه پرداخت'},
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();

  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <div>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      key={index}
                      hover
                      style={{
                        backgroundColor:
                          !row.exists && row.status === 'ORDERED'
                            ? '#f7d28b'
                            : !row.exists && row.status === 'SUPPLYING'
                            ? '#FFFF8F'
                            : 'white',
                      }}
                      role="checkbox"
                      tabIndex={-1}
                    >
                      <StyledTableCell id={labelId} scope="row" align="center">
                        {row.customerName}
                      </StyledTableCell>
                      <StyledTableCell align="center" data-cy="row-id">
                        {row.id}
                        {row.warehouseType == 'MULTI' && (
                          <span style={{padding: '5px'}}>
                            <Icon
                              color={theme.palette.green.main}
                              path={mdiPlusBoxMultiple}
                              size={1}
                            />
                          </span>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center" data-cy="row-id">
                        {row.trackingCode}
                      </StyledTableCell>
                      <StyledTableCell align="center" data-cy="row-id">
                        {translate(row.deliveryType)}
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        <span>
                          {jMoment(row.deliveryDate, 'YYYY/M/D')
                            .locale('fa')
                            .format('jYY/jMM/jDD')}
                        </span>
                        &nbsp; &nbsp;- &nbsp; &nbsp;
                        <span>
                          {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                            .locale('fa')
                            .format('HH')}
                        `}
                          {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                            .locale('fa')
                            .format('HH')}`}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <span className={classes.renderValue}>
                          {translate(row.status, 'order')}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                      {translate(row.paymentType,'cod')}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <div>
                          <Link to={`/orders/${row.id}`}>
                            <IconButton
                              aria-label="details"
                              size="small"
                              title="جزئیات سفارش"
                              data-cy="details"
                            >
                              <Icon
                                path={mdiFileDocumentOutline}
                                size={1}
                                color={theme.palette.green.main}
                              />
                            </IconButton>
                          </Link>
                          {(row.status === 'ORDERED' ||
                            row.status === 'SUPPLYING') && (
                            <Can
                              role={authContext.user.role}
                              perform="seller_orders:list"
                              yes={() => (
                                <Link to={`/edit-order/${row.id}`}>
                                  <IconButton size="small" title="ویرایش سفارش">
                                    <Icon
                                      color={theme.palette.blue.main}
                                      path={mdiPencilOutline}
                                      size={1}
                                    />
                                  </IconButton>
                                </Link>
                              )}
                            />
                          )}
                          {/* <Link to={`/orders/${row.id}/allLogs`}>
                            <IconButton size="small" title="تاریخچه سفارش">
                              <Icon path={mdiHistory} size={1} />
                            </IconButton>
                          </Link> */}
                          <IconButton
                            aria-label="cancel"
                            title={
                              row.status === 'CANCELLED'
                                ? 'لغو شده'
                                : 'لغو سفارش'
                            }
                            size="small"
                            style={{pointerEvents: 'fill'}}
                            disabled={
                              row.status === 'DELIVERED' ||
                              row.status === 'ARRIVED' ||
                              row.status === 'DELIVERED_TO_POST' ||
                              row.status === 'LOADED' ||
                              row.status === 'CANCELLED'
                            }
                            onClick={() =>
                              props.handleOpenConfirmCancel(row.id)
                            }
                          >
                            <Icon
                              path={mdiClose}
                              size={1}
                              color={
                                row.status !== 'CANCELLED'
                                  ? theme.palette.error.main
                                  : theme.palette.gray.main
                              }
                            />
                          </IconButton>
                        </div>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
}
